import { Command, Option } from "clipanion";
import { AzureCliCredential } from "@azure/identity";
import type { TokenCredential } from "@azure/identity";
import { TableClient } from "@azure/data-tables";
import { AIProjectClient } from "@azure/ai-projects";
import { LogsQueryClient, LogsQueryResultStatus } from "@azure/monitor-query";
import {
  fetchLedgerRows,
  fetchEnrichment,
  type LedgerRow,
} from "@m8t-stack/agent-ledger/read";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors, renderJson, resolveOutputMode } from "../../lib/output.js";
import { resolveDreamContext, type DreamContext } from "../../lib/dream-context.js";
import {
  runPipeline,
  runDream,
  makeLedgerSource,
  makeConversationSource,
  makeTraceSource,
  makeTableCursor,
  makeGitDataWriter,
  makeFoundryModelClient,
  formatCursorPreview,
  parseDreamerConfig,
  DEFAULT_SAFETY_LAG_SECONDS,
  DEFAULT_DREAM_MODEL,
  ENGINE_VERSION,
  type NormalizedDreamInput,
  type DreamCursor,
  type DreamResult,
  type DreamDeps,
} from "@m8t-stack/brain-engine";
import { mintInstallationToken } from "@m8t-stack/github-app-auth";
import { parseBrainLink, isAppMode } from "@m8t-stack/api-contract";
import { getAgentVersion } from "../../lib/foundry-agent-get.js";

/** The AgentLedger Table Storage table — the ledger rows live here (PK = lowercase agentName). */
const LEDGER_TABLE_NAME = "AgentLedger";

/** Resp ids are `resp_<alnum>`; guard the KQL `in()` interpolation (same as the traces client). */
const SAFE_ID = /^[A-Za-z0-9_]+$/;

/** A validated Foundry conversation id (`conv_<alnum>`); makeTraceSource re-validates, but we
 *  only ever surface a clean one. */
const CONV_ID = /^conv_[A-Za-z0-9]+$/;

/**
 * Resolution seam. The command builds a concrete `RunDreamDeps` from
 * `AzureCliCredential` + ARM discovery at the edge (`defaultDeps`); unit tests
 * assign `cmd.deps` directly with in-memory fakes so no live Azure is touched.
 */
export interface RunDreamDeps {
  resolveContext(opts: {
    worker: string;
    subscription?: string;
    endpoint?: string;
  }): Promise<DreamContext>;
  runPipeline(opts: {
    worker: string;
    physicalPks: string[];
    since?: string;
    reset: boolean;
    safetyLagSeconds: number;
    now: Date;
    ledger: ReturnType<typeof makeLedgerSource>;
    conversations: ReturnType<typeof makeConversationSource>;
    traces: ReturnType<typeof makeTraceSource>;
    cursor: ReturnType<typeof makeTableCursor>;
  }): Promise<NormalizedDreamInput>;
  readCursor(worker: string): Promise<DreamCursor>;
  /**
   * The non-dry live leg (F3). Harvests (runPipeline, dryRun:false so
   * input.advance is computed but NOT committed — Task 1), builds the write-side
   * DreamDeps (Foundry model client + git-data BrainRepoWriter), and calls the
   * engine `runDream` — which commits the cursor advance ONLY after a confirmed
   * brain write (the CRITICAL F1 fix). Unit tests inject a fake so no Azure /
   * GitHub is touched; the live builder lives in `defaultDeps`.
   */
  runDream(opts: {
    worker: string;
    physicalPks: string[];
    since?: string;
    reset: boolean;
    safetyLagSeconds: number;
    now: Date;
    ledger: ReturnType<typeof makeLedgerSource>;
    conversations: ReturnType<typeof makeConversationSource>;
    traces: ReturnType<typeof makeTraceSource>;
    cursor: ReturnType<typeof makeTableCursor>;
  }): Promise<DreamResult>;
}

function isoOrThrow(value: string, now: Date): string {
  // Strict ISO-8601: Date.parse is lenient, so re-stringify and require the
  // input to round-trip to a real instant; reject anything in the future.
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) {
    throw new LocalCliError({
      code: "DREAM_SINCE_INVALID",
      message: `--since '${value}' is not a valid ISO-8601 timestamp.`,
      hint: "Expected e.g. 2026-06-01T00:00:00Z.",
    });
  }
  if (ms > now.getTime()) {
    throw new LocalCliError({
      code: "DREAM_SINCE_FUTURE",
      message: `--since '${value}' is in the future.`,
      hint: "Pick a timestamp at or before now.",
    });
  }
  return new Date(ms).toISOString();
}

/**
 * PII gate for `--output json` (FIX 3): replace every transcript body
 * (`conversations[].items[].text`) with a length-preserving redaction marker so
 * counts/roles/timestamps still validate, but no transcript content is
 * serialized. ONLY `items[].text` is the gated transcript-body class —
 * `attribution.user.{source,ref,display}` is in-contract metadata (DESIGN §5)
 * and is left intact. Pure (returns a shallow copy; never mutates `input`).
 */
function redactTranscripts(input: NormalizedDreamInput): NormalizedDreamInput {
  return {
    ...input,
    conversations: input.conversations.map((c) => ({
      ...c,
      items: c.items.map((it) => ({ ...it, text: `<redacted: ${it.text.length.toString()} chars>` })),
    })),
  };
}

export class DreamRunCommand extends M8tCommand {
  static paths = [["dream", "run"]];
  static usage = Command.Usage({
    description: "Dry-run the brain consumption pipeline for one worker (no model call, no writes).",
    details:
      "Builds AzureCliCredential + resolves the Foundry project, ledger table, and Log " +
      "Analytics workspace, runs the consumption pipeline, and prints the skip-ledger, the " +
      "partition invariant, and harvest stats. Transcripts are metadata-only unless " +
      "--show-transcripts is passed.",
  });

  worker = Option.String("--worker", { description: "Worker (canonical name) to harvest. Required." });
  dryRun = Option.Boolean("--dry-run", false, { description: "Read-only harvest; no model call, no writes." });
  since = Option.String("--since", { description: "ISO-8601 start override (rejected if malformed or future)." });
  reset = Option.Boolean("--reset", false, { description: "Ignore the stored cursor; read from the beginning." });
  showTranscripts = Option.Boolean("--show-transcripts", false, {
    description: "Print transcript bodies (default: metadata only).",
  });
  subscription = Option.String("--subscription");
  endpoint = Option.String("--endpoint");
  output = Option.String("--output");

  // Resolution seam — overridden by tests; built lazily at runtime otherwise.
  deps?: RunDreamDeps;

  async executeCommand(): Promise<number> {
    const worker = typeof this.worker === "string" ? this.worker : undefined;
    if (!worker) {
      throw new LocalCliError({ code: "USAGE", message: "--worker <name> is required." });
    }

    const since =
      typeof this.since === "string" ? isoOrThrow(this.since, new Date()) : undefined;
    // Clipanion leaves an Option descriptor on the property until argv is parsed;
    // tests set the literal boolean. Only `=== true` distinguishes "flag passed"
    // from the descriptor (which is truthy) — `!this.reset` would misread it.
    const reset = this.reset === true;

    const outputFlag =
      this.output === "json" || this.output === "auto" || this.output === "pretty"
        ? (this.output)
        : "pretty";
    const mode = resolveOutputMode(outputFlag, this.context.stdout as NodeJS.WriteStream);

    // ── Non-dry-run: the F3 live leg (model call + brain write). ─────────────
    // Harvest → propose → apply gates → atomic git-data write → commit the
    // cursor advance ONLY after the write succeeds (CRITICAL F1). The heavy
    // wiring is in deps.runDream (defaultDeps); tests inject a fake.
    // `!== true` distinguishes "flag passed" from the clipanion Option descriptor.
    if (this.dryRun !== true) {
      const liveDeps = this.deps ?? defaultDeps();
      const liveContext = await liveDeps.resolveContext({
        worker,
        subscription: typeof this.subscription === "string" ? this.subscription : undefined,
        endpoint: typeof this.endpoint === "string" ? this.endpoint : undefined,
      });
      const liveCursor = await liveDeps.readCursor(liveContext.worker);
      const result = await liveDeps.runDream({
        worker: liveContext.worker,
        physicalPks: liveContext.physicalPks,
        since,
        reset,
        safetyLagSeconds: liveCursor.safetyLagSeconds || DEFAULT_SAFETY_LAG_SECONDS,
        now: new Date(),
        ...buildSources(liveContext, liveDeps),
      });

      this.context.stdout.write(
        `${colors.field("dream run (live)")} worker=${result.worker}  ` +
          `applied ${String(result.applied.length)}  ` +
          `${result.advanced ? colors.success("advanced") : colors.dim("not advanced")}\n`,
      );
      const changed = result.applied.filter(
        (e): e is Extract<DreamResult["applied"][number], { kind: "changed" }> => e.kind === "changed",
      );
      for (const e of changed) {
        this.context.stdout.write(
          `    ${colors.dim(e.verb.toUpperCase())} ${e.path}  ` +
            `${colors.dim(`(${e.evidence.join(", ") || "-"})`)}\n`,
        );
      }
      const cost = result.digest.find(
        (d): d is Extract<DreamResult["digest"][number], { kind: "cost" }> => d.kind === "cost",
      );
      if (cost) {
        this.context.stdout.write(
          `    ${colors.dim(`cost: ${cost.model} in ${String(cost.inputTokens)} out ${String(cost.outputTokens)} (${String(cost.ms)}ms)`)}\n`,
        );
      }
      // The locked DigestEntry folds no-op|rejected|failure into ONE member whose
      // `kind` is that union, so Extract<…,{kind:"failure"}> alone is `never` —
      // intersect with the literal to recover the failure shape.
      const failures = result.digest.filter(
        (d): d is Extract<DreamResult["digest"][number], { kind: "no-op" | "rejected" | "failure" }> & { kind: "failure" } =>
          d.kind === "failure",
      );
      for (const f of failures) {
        this.context.stdout.write(`    ${colors.error("failure")}: ${f.detail}\n`);
      }
      return 0;
    }

    const deps = this.deps ?? defaultDeps();
    const context = await deps.resolveContext({
      worker,
      subscription: typeof this.subscription === "string" ? this.subscription : undefined,
      endpoint: typeof this.endpoint === "string" ? this.endpoint : undefined,
    });

    const now = new Date();
    const cursor = await deps.readCursor(context.worker);
    const safetyLagSeconds = cursor.safetyLagSeconds;

    const input = await deps.runPipeline({
      worker: context.worker,
      physicalPks: context.physicalPks,
      since,
      reset,
      safetyLagSeconds,
      now,
      ...buildSources(context, deps),
    });

    // Partition invariant — the structural no-silent-drop guarantee (DESIGN §4).
    const skips = input.skipLedger.reduce((sum, e) => sum + e.count, 0);
    const lhs = input.stats.consumed + input.stats.recovered + skips;
    const invariantOk = lhs === input.stats.groups;

    if (mode === "json") {
      // PII gate (FIX 3): transcript BODIES (items[].text) are the gated class —
      // strip them from the serialized JSON unless --show-transcripts is passed
      // (the pretty path already gates them). Roles/timestamps + the in-contract
      // attribution.user metadata (DESIGN §5) STAY.
      // PII gate: redact unless the flag was *explicitly* passed. `=== true`
      // distinguishes it from the truthy clipanion Option descriptor — a bare
      // truthy check would leak transcript bodies when the flag is absent.
      const safeInput = this.showTranscripts === true ? input : redactTranscripts(input);
      this.context.stdout.write(
        renderJson({
          input: safeInput,
          cursor,
          invariant: {
            ok: invariantOk,
            consumed: input.stats.consumed,
            recovered: input.stats.recovered,
            skipped: skips,
            groups: input.stats.groups,
          },
        }) + "\n",
      );
      if (!invariantOk) {
        throw new LocalCliError({
          code: "DREAM_INVARIANT_VIOLATED",
          message: `partition invariant failed: ${lhs.toString()} != ${input.stats.groups.toString()}`,
        });
      }
      return 0;
    }

    // ── pretty ──────────────────────────────────────────────────────────────
    this.context.stdout.write(
      `${colors.field(`dream run --dry-run`)} worker=${input.worker} ` +
        `window ${input.window.from ?? "(beginning)"} → ${input.window.to}\n`,
    );
    this.context.stdout.write(
      `  ${colors.dim(
        `stats: ledgerRows ${input.stats.ledgerRows.toString()}, groups ${input.stats.groups.toString()}, ` +
          `consumed ${input.stats.consumed.toString()}, recovered ${input.stats.recovered.toString()}, ` +
          `skipped ${input.stats.skipped.toString()}, ${input.stats.latencyMs.toString()}ms`,
      )}\n`,
    );

    this.context.stdout.write(`  ${colors.field("conversations")} (${input.conversations.length.toString()}):\n`);
    for (const c of input.conversations) {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- left can be "" (join of an empty tools array); the "-" fallback must fire for empty, not just undefined
      const tools = c.enrichment?.tools.map((t) => t.name).join(",") || "-";
      this.context.stdout.write(
        `    ${c.conversationId}  [${c.source}]  rounds ${c.rounds.toString()}  items: ${c.items.length.toString()}` +
          (c.recoveredVia ? "  (recovered via trace)" : "") +
          `  ${colors.dim(`tools=${tools}`)}\n`,
      );
      // PII gate (pretty path): only print bodies when the flag is explicitly set.
      if (this.showTranscripts === true) {
        for (const item of c.items) {
          this.context.stdout.write(`      ${colors.dim(`${item.role}:`)} ${item.text}\n`);
        }
      }
    }

    this.context.stdout.write(`  ${colors.field("skip ledger")}:\n`);
    for (const e of input.skipLedger) {
      this.context.stdout.write(
        `    ${e.reason.padEnd(20)} ${String(e.count).padStart(5)}  ` +
          colors.dim(`e.g. ${e.sampleIds.slice(0, 3).join(", ") || "-"}`) +
          `${e.lastError ? colors.dim(`  lastError: ${e.lastError}`) : ""}\n`,
      );
    }

    // DESIGN §2.5 — show the watermark the run WOULD commit (from input.advance),
    // computed but NEVER written on the dry path. Route through the shared engine
    // formatter so the format is identical to the live post-write print.
    this.context.stdout.write(`  ${colors.field("cursor")} (dry-run — nothing written):\n`);
    const wouldAdvance: DreamCursor = {
      worker: input.advance.worker,
      watermarks: input.advance.watermarks,
      safetyLagSeconds,
      engineVersion: ENGINE_VERSION,
    };
    for (const [pk, wm] of Object.entries(wouldAdvance.watermarks)) {
      this.context.stdout.write(
        `    would advance ${pk.padEnd(16)} → ts ${wm.ts}  ` +
          `${colors.dim(`(${wm.consumedRowKeys.length.toString()} consumed rows, safetyLag ${safetyLagSeconds.toString()}s)`)}\n`,
      );
    }
    this.context.stdout.write(`  ${colors.dim(formatCursorPreview(wouldAdvance))}\n`);

    const mark = invariantOk ? colors.success("OK") : colors.error("VIOLATED");
    this.context.stdout.write(
      `  ${colors.field("invariant")} consumed + recovered + Σ(skips) == groups: ` +
        `${input.stats.consumed.toString()} + ${input.stats.recovered.toString()} + ${skips.toString()} == ${input.stats.groups.toString()}  ${mark}\n`,
    );

    if (!invariantOk) {
      throw new LocalCliError({
        code: "DREAM_INVARIANT_VIOLATED",
        message: `partition invariant failed: ${lhs.toString()} != ${input.stats.groups.toString()} — a group was silently dropped.`,
      });
    }
    return 0;
  }
}

/** The 4 concrete brain-engine sources for the live path, built from a resolved context. */
interface LiveSources {
  ledger: ReturnType<typeof makeLedgerSource>;
  conversations: ReturnType<typeof makeConversationSource>;
  traces: ReturnType<typeof makeTraceSource>;
  cursor: ReturnType<typeof makeTableCursor>;
}

/**
 * KQL: one responseId → its `gen_ai.conversation.id`, over the worker's Log
 * Analytics workspace. The agent-ledger traces client exposes no conv-id
 * recovery querier (it joins the other way — conv→spans for enrichment), so
 * this is the only small KQL the live wiring writes itself. Matches the
 * traces-client doctrine: AppDependencies, DependencyType=="AI",
 * todynamic(Properties) (never `has` — term-tokenized ids miss sub-tokens),
 * exact `resp ==` equality. Returns the first non-empty conv id, validated to
 * `conv_…` (a stray/empty span must not mis-attribute an a2a turn — DESIGN §6).
 */
function makeConvIdFn(
  credential: TokenCredential,
  workspaceId: string,
): (respId: string, from: string, to: string) => Promise<string | null> {
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- LogsQueryClient moved to @azure/monitor-query-logs; migration is a separate dependency-update PR
  const client = new LogsQueryClient(credential);
  return async (respId, from, to) => {
    if (!SAFE_ID.test(respId)) return null;
    const kql = `
AppDependencies
| where DependencyType == "AI"
| extend P = todynamic(Properties)
| extend resp = tostring(P["gen_ai.response.id"]), conv = tostring(P["gen_ai.conversation.id"])
| where resp == '${respId}'
| where isnotempty(conv)
| project conv
| take 1`;
    const result = await client.queryWorkspace(workspaceId, kql, {
      startTime: new Date(from),
      endTime: new Date(to),
    });
    if (result.status !== LogsQueryResultStatus.Success || result.tables.length === 0) return null;
    const table = result.tables[0];
    if (table.rows.length === 0) return null;
    const row = table.rows[0];
    const idx = table.columnDescriptors.findIndex((c) => c.name === "conv");
    const rawConv = idx >= 0 ? row[idx] : undefined;
    const conv = typeof rawConv === "string" ? rawConv : "";
    return CONV_ID.test(conv) ? conv : null;
  };
}

/**
 * Build the 4 live brain-engine sources from a resolved `DreamContext` + the
 * operator credential. All four read-only (the dry-run path never calls
 * cursor.advance):
 *   - ledger:        TableClient(AgentLedger) → fetchLedgerRows per physical PK
 *                    (auto-paginated, NO $top — DESIGN §0.4); rows carry rowKey.
 *   - conversations: AIProjectClient(projectEndpoint).getOpenAIClient() →
 *                    conversations.items.list (same construction as the gateway).
 *   - traces:        fetchEnrichment bound to credential+workspaceId for enrich;
 *                    a small conv-id KQL for recovery.
 *   - cursor:        TableClient(BrainDreamCursor) via the engine's makeTableCursor.
 */
function buildLiveSources(context: DreamContext, credential: TokenCredential): LiveSources {
  const ledgerClient = new TableClient(context.ledgerTableEndpoint, LEDGER_TABLE_NAME, credential);

  // LedgerReadFn: (pk, sinceTs) → rows for ONE physical PK from the inclusive
  // floor. "" (from the beginning) becomes an empty lower bound; the upper bound
  // is open (a far-future ISO) so the watermark/overlap math owns the window.
  const ledger = makeLedgerSource(async (pk: string, sinceTs: string): Promise<LedgerRow[]> =>
    fetchLedgerRows(
      { workers: [pk], source: "all", from: sinceTs, to: "9999-12-31T23:59:59.999Z" },
      ledgerClient,
    ),
  );

  const openai = new AIProjectClient(context.projectEndpoint, credential).getOpenAIClient();
  const conversations = makeConversationSource(openai);

  const traces = makeTraceSource({
    enrichFn: (respIds, from, to) =>
      fetchEnrichment(credential, context.workspaceId, respIds, from, to),
    convIdFn: makeConvIdFn(credential, context.workspaceId),
  });

  const cursor = makeTableCursor(credential, context.ledgerTableEndpoint);

  return { ledger, conversations, traces, cursor };
}

/**
 * Build the write-side DreamDeps for the live `m8t dream run` (DESIGN §2.4, §4).
 * Resolves the worker's brain link (repo + branch + installationId) from the
 * agent's `metadata.brain`, builds the git-data BrainRepoWriter authed by a
 * freshly-minted installation token, reads the dreamer model from the brain's
 * own `.m8t/brain.yaml`, and constructs the Foundry model client. App-mode only:
 * a brain with no installationId cannot mint and is rejected loudly.
 *
 * This is the live edge — NEVER reached by the unit tests (which inject
 * `cmd.deps.runDream`), so it carries no test seam; it only has to typecheck and
 * compose the real collaborators. The live write legs are smoke-verified
 * (Task 28) once the operator grants Key Vault Secrets read.
 */
async function buildWriteDeps(
  context: DreamContext,
  credential: TokenCredential,
  cursor: ReturnType<typeof makeTableCursor>,
): Promise<DreamDeps> {
  const agent = await getAgentVersion({
    credential,
    projectEndpoint: context.projectEndpoint,
    agentName: context.worker,
  });
  const link = parseBrainLink(agent.metadata);
  if (!link) {
    throw new LocalCliError({
      code: "DREAM_NOT_LINKED",
      message: `Worker '${context.worker}' has no brain link (metadata.brain absent or malformed) — cannot write.`,
      hint: "Run `m8t brain link --worker <name> --repo <owner/name>` first.",
    });
  }
  if (!isAppMode(link)) {
    throw new LocalCliError({
      code: "DREAM_NOT_APP_MODE",
      message: `Worker '${context.worker}' brain link has no installationId (PAT/in-container mode) — the dreamer writes via a minted App token only.`,
      hint: "Re-link in App mode so an installation token can be minted.",
    });
  }
  const kvUri = process.env.KEY_VAULT_URI;
  if (!kvUri) {
    throw new LocalCliError({
      code: "DREAM_NO_KEY_VAULT",
      message: "KEY_VAULT_URI is not set — cannot mint a GitHub App installation token for the brain write.",
      hint: "Export KEY_VAULT_URI=https://<vault>.vault.azure.net (the gateway's Key Vault).",
    });
  }
  const installationId = link.installationId;
  const repository = link.repo;
  const mintToken = (): Promise<string> =>
    mintInstallationToken({ credential, kvUri, installationId, repository }).then((m) => m.token);

  const brain = makeGitDataWriter({ repository, branch: link.branch, mintToken });

  // The dreamer model comes from the brain's own .m8t/brain.yaml (M8T_DREAM_MODEL
  // overrides; DEFAULT_DREAM_MODEL when absent). Read it through the writer so we
  // hit the same repo/branch we write to. A read failure degrades to the default.
  let model = DEFAULT_DREAM_MODEL;
  try {
    const rawYaml = await brain.readFile(".m8t/brain.yaml");
    if (rawYaml) model = parseDreamerConfig(rawYaml, process.env).model;
    else if (process.env.M8T_DREAM_MODEL) model = process.env.M8T_DREAM_MODEL;
  } catch {
    if (process.env.M8T_DREAM_MODEL) model = process.env.M8T_DREAM_MODEL;
  }

  const openai = new AIProjectClient(context.projectEndpoint, credential).getOpenAIClient();

  return {
    model: makeFoundryModelClient(openai, model),
    modelName: model, // surface the resolved name for the cost digest (DESIGN §4.1/§9)
    brain,
    cursor,
    clock: () => new Date(),
    logger: {
      info: (o) => { console.info("[dream]", o); },
      warn: (o) => { console.warn("[dream]", o); },
      error: (o) => { console.error("[dream]", o); },
    },
  };
}

/**
 * Resolve the live sources for the command. `defaultDeps` stashes a builder on a
 * private field so `buildSources` (called by the command with the resolved
 * context) and `readCursor` share ONE set of sources — in particular one cursor
 * client. The unit tests inject `cmd.deps` directly and never reach this.
 */
const LIVE_SOURCES = Symbol("liveSources");
type DepsWithLive = RunDreamDeps & {
  [LIVE_SOURCES]?: (context: DreamContext) => LiveSources;
};

/**
 * Build the concrete brain-engine sources from a resolved context. The live
 * `defaultDeps` carries a real source builder under `[LIVE_SOURCES]`; the unit
 * tests inject `cmd.deps` with a fake `runPipeline` that ignores these sources
 * entirely, so when no live builder is present we hand back an empty object (the
 * spread is harmless — the fake never reads it). The seam stays intact.
 */
function buildSources(context: DreamContext, deps: RunDreamDeps): LiveSources {
  const build = (deps as DepsWithLive)[LIVE_SOURCES];
  if (build) return build(context);
  return {} as LiveSources; // test seam: fake runPipeline discards these
}

/**
 * Build the live deps from AzureCliCredential + ARM discovery. The dry-run CLI
 * authenticates as the operator (Storage Table Data Contributor + Log Analytics
 * Reader, granted 2026-06-13 — DESIGN §0.2).
 *
 * `overrides` is a test-only seam (defaults to the live wiring): tests pass a
 * fake `resolveContext` + `buildSources` so the closure's `readCursor`/cursor
 * forwarding can be exercised without touching Azure. Production calls
 * `defaultDeps()` with no args — identical behavior.
 */
export function defaultDeps(overrides?: {
  resolveContext?: (opts: { worker: string; subscription?: string; endpoint?: string }) => Promise<DreamContext>;
  buildSources?: (context: DreamContext, credential: TokenCredential) => LiveSources;
  credential?: TokenCredential;
}): RunDreamDeps {
  const credential = overrides?.credential ?? new AzureCliCredential();
  const build = overrides?.buildSources ?? buildLiveSources;
  // Memoize the sources per resolved context so readCursor + buildSources share
  // the same cursor/ledger/conversation/trace clients within one invocation.
  let cached: { context: DreamContext; sources: LiveSources } | undefined;
  function sourcesFor(context: DreamContext): LiveSources {
    if (cached?.context !== context) {
      cached = { context, sources: build(context, credential) };
    }
    return cached.sources;
  }

  const deps: DepsWithLive = {
    async resolveContext(opts) {
      // Resolve the endpoints, then eagerly build the live sources so the
      // command's readCursor (which runs before buildSources) shares them.
      const context = overrides?.resolveContext
        ? await overrides.resolveContext(opts)
        : await resolveDreamContext({ credential, ...opts });
      sourcesFor(context);
      return context;
    },
    async runPipeline(opts) {
      // Adapt the CLI-edge flat opts to the engine's RunPipelineArgs (nested
      // `sources`, `trace` not `traces`, explicit `dryRun`). The concrete
      // sources are injected by the command via `buildSources`.
      return runPipeline({
        worker: opts.worker,
        now: opts.now,
        dryRun: true,
        since: opts.since,
        reset: opts.reset,
        sources: {
          ledger: opts.ledger,
          conversations: opts.conversations,
          trace: opts.traces,
          cursor: opts.cursor,
        },
      });
    },
    async readCursor(worker) {
      if (!cached) {
        throw new LocalCliError({
          code: "DREAM_INTERNAL",
          message: "readCursor called before resolveContext.",
        });
      }
      // Forward the canonical worker (FIX 5) — TableCursor.read keys the cursor
      // row on it; an empty string would point-read the wrong (empty) row.
      return cached.sources.cursor.read(worker);
    },
    async runDream(opts) {
      if (!cached) {
        throw new LocalCliError({
          code: "DREAM_INTERNAL",
          message: "runDream called before resolveContext.",
        });
      }
      // 1. Harvest. dryRun:false so the engine COMPUTES input.advance — but does
      //    NOT commit it (Task 1: runPipeline no longer commits; runDream commits
      //    the advance only AFTER a confirmed brain write — the CRITICAL F1 fix).
      const input = await runPipeline({
        worker: opts.worker,
        now: opts.now,
        dryRun: false,
        since: opts.since,
        reset: opts.reset,
        sources: {
          ledger: opts.ledger,
          conversations: opts.conversations,
          trace: opts.traces,
          cursor: opts.cursor,
        },
      });
      // 2. Build the write-side deps (Foundry model client + git-data writer over
      //    a freshly-minted installation token), then run the engine dreamer with
      //    its default propose+apply seams (DESIGN's 2-arg edge contract).
      const writeDeps = await buildWriteDeps(cached.context, credential, opts.cursor);
      return runDream(input, writeDeps);
    },
    [LIVE_SOURCES]: sourcesFor,
  };

  return deps;
}
